import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReplayIcon from '@mui/icons-material/Replay';
import UndoIcon from '@mui/icons-material/Undo';
import { formatDistanceToNow } from 'date-fns';

import type { DashboardCustomer } from '@models/customers';
import type { PaymentCurrency } from '@models/payments';
import {
  buildCurrencyLookup,
  formatAmountWithCurrency,
  formatCustomerPaymentAmount,
  formatDebtAmount,
  formatShortDate,
  getActiveSubscriptions,
  getCustomerPaymentMethodDigits,
  getCustomerPaymentTypeLabel,
  getPaymentMethodLabel,
  getPaymentMethodSubLabel,
  getScheduledPaymentAmountLabel,
  getScheduledPaymentStatusLabel,
  getScheduledPaymentStatusTone,
  getScheduledPaymentTimeLeft,
  getSubscriptionLabel,
  humanizeLabel,
} from '@features/customers/helpers';
import { getUiPaymentStatus } from '@features/payments/helpers';

import PaymentStatusBadge from '@components/Payments/PaymentStatusBadge';
import StatusChip from '@components/ui/StatusChip';
import SubscriptionBadge from '../SubscriptionBadge';
import PaymentMethodBrandBadge from './PaymentMethodBrandBadge';
import s from './CustomerDetailPanel.module.css';

type ActivityKind = 'paid' | 'failed' | 'subscription' | 'debt' | 'recovery' | 'refund';

type ActivityEvent = {
  key: string;
  kind: ActivityKind;
  title: string;
  detail: string;
  at: string;
};

const ACTIVITY_ICONS: Record<ActivityKind, typeof CheckCircleOutlineIcon> = {
  paid: CheckCircleOutlineIcon,
  failed: ErrorOutlineIcon,
  subscription: AutorenewIcon,
  debt: WarningAmberIcon,
  recovery: ReplayIcon,
  refund: UndoIcon,
};

const ACTIVITY_ICON_CLASSES: Record<ActivityKind, string> = {
  paid: 'activityIconPaid',
  failed: 'activityIconFailed',
  subscription: 'activityIconSubscription',
  debt: 'activityIconDebt',
  recovery: 'activityIconRecovery',
  refund: 'activityIconRefund',
};

function buildActivity(
  customer: DashboardCustomer,
  currencyById: Map<string, PaymentCurrency>,
): ActivityEvent[] {
  const events: ActivityEvent[] = [];

  for (const payment of customer.payments ?? []) {
    const digits = getCustomerPaymentMethodDigits(customer, payment);
    const typeLabel = getCustomerPaymentTypeLabel(payment.collectionType);

    if (getUiPaymentStatus(payment) === 'failed') {
      events.push({
        key: `payment-${payment.id}`,
        kind: 'failed',
        title: `Payment failed · ${formatCustomerPaymentAmount(payment, currencyById)}`,
        detail: [payment.providerDeclineMessage || typeLabel, formatShortDate(payment.createdAt)]
          .filter(Boolean)
          .join(' · '),
        at: payment.createdAt,
      });
    } else {
      events.push({
        key: `payment-${payment.id}`,
        kind: 'paid',
        title: `Paid ${formatCustomerPaymentAmount(payment, currencyById)}`,
        detail: [typeLabel, digits, formatShortDate(payment.createdAt)].filter(Boolean).join(' · '),
        at: payment.createdAt,
      });
    }
  }

  for (const sub of customer.subscriptions ?? []) {
    // Migrated subscriptions carry their original start in startedAt; createdAt is the migration moment.
    const startedAt = sub.startedAt ?? sub.createdAt;
    events.push({
      key: `sub-start-${sub.id}`,
      kind: 'subscription',
      title: 'Subscription started',
      detail: [sub.paymentDescriptor, formatShortDate(startedAt)].filter(Boolean).join(' · '),
      at: startedAt,
    });
    if (sub.canceledAt) {
      events.push({
        key: `sub-cancel-${sub.id}`,
        kind: 'subscription',
        title: 'Subscription canceled',
        detail: [
          sub.paymentDescriptor,
          sub.canceledBy ? `by ${sub.canceledBy}` : '',
          formatShortDate(sub.canceledAt),
        ]
          .filter(Boolean)
          .join(' · '),
        at: sub.canceledAt,
      });
    }
  }

  for (const recovery of customer.subscriptionRecoveries ?? []) {
    // Recovery amounts are minor units in the subscription's currency; the recovery row carries no
    // currency reference of its own, so resolve it through its subscription.
    const subscription = customer.subscriptions?.find((sub) => sub.id === recovery.subscriptionId);
    const paymentDescriptor = subscription?.paymentDescriptor;

    events.push({
      key: `recovery-${recovery.id}`,
      kind: 'recovery',
      title: `Recovery started · ${formatAmountWithCurrency(recovery.originalChargeAmount, subscription?.currencyId, currencyById)}`,
      detail: [paymentDescriptor, formatShortDate(recovery.createdAt)].filter(Boolean).join(' · '),
      at: recovery.createdAt,
    });
    if (recovery.recoveredAt) {
      events.push({
        key: `recovery-done-${recovery.id}`,
        kind: 'paid',
        title: `Recovered · ${formatAmountWithCurrency(recovery.totalRecoveredAmount, subscription?.currencyId, currencyById)}`,
        detail: [paymentDescriptor, formatShortDate(recovery.recoveredAt)]
          .filter(Boolean)
          .join(' · '),
        at: recovery.recoveredAt,
      });
    }
  }

  for (const refund of customer.refunds ?? []) {
    const reasons = (refund.reasons ?? []).map(humanizeLabel).join(', ');
    const amountLabel = formatAmountWithCurrency(refund.amount, refund.currencyId, currencyById);

    events.push({
      key: `refund-${refund.id}`,
      kind: 'refund',
      title: `Refund requested · ${amountLabel}`,
      detail: [reasons, formatShortDate(refund.createdAt)].filter(Boolean).join(' · '),
      at: refund.createdAt,
    });
    if (refund.completedAt) {
      events.push({
        key: `refund-done-${refund.id}`,
        kind: 'refund',
        title: `Refunded · ${amountLabel}`,
        detail: [reasons, formatShortDate(refund.completedAt)].filter(Boolean).join(' · '),
        at: refund.completedAt,
      });
    }
  }

  for (const debt of customer.debtCollections ?? []) {
    const paymentDescriptor = customer.subscriptions?.find(
      (sub) => sub.id === debt.subscriptionId,
    )?.paymentDescriptor;

    events.push({
      key: `debt-${debt.id}`,
      kind: 'debt',
      title: `Debt collection started · ${formatDebtAmount(debt.amount, debt, currencyById)}`,
      detail: [paymentDescriptor, formatShortDate(debt.createdAt)].filter(Boolean).join(' · '),
      at: debt.createdAt,
    });

    if (debt.collectedAt) {
      events.push({
        key: `debt-collected-${debt.id}`,
        kind: 'paid',
        title: `Debt collected · ${formatDebtAmount(debt.collectedAmount, debt, currencyById)}`,
        detail: [paymentDescriptor, formatShortDate(debt.collectedAt)].filter(Boolean).join(' · '),
        at: debt.collectedAt,
      });
    }
  }

  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

type CustomerOverviewTabProps = {
  customer: DashboardCustomer;
};

function CustomerOverviewTab({ customer }: CustomerOverviewTabProps) {
  const recentPayments = customer.payments ?? [];
  const recentScheduledPayments = (customer.scheduledPayments ?? []).slice(0, 10);
  const activeSubscriptions = getActiveSubscriptions(customer);
  const methods = customer.customerPaymentMethods ?? [];
  const currencyById = buildCurrencyLookup(customer);
  const activity = buildActivity(customer, currencyById);
  // Debt/recovery scheduled rows show "charge of remaining left"; look up their balances by id.
  const debtById = new Map((customer.debtCollections ?? []).map((debt) => [debt.id, debt]));
  const recoveryById = new Map(
    (customer.subscriptionRecoveries ?? []).map((recovery) => [recovery.id, recovery]),
  );

  return (
    <div className={s.overviewGrid}>
      <section className={s.card}>
        <header className={s.cardHeader}>
          <h3 className={s.cardTitle}>Recent payments</h3>
        </header>
        {recentPayments.length === 0 ? (
          <div className={s.cardEmpty}>No payments yet.</div>
        ) : (
          <div className={s.cardBody}>
            <table className={s.miniTable}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th className={s.alignRight}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentPayments.map((payment) => {
                  const digits = getCustomerPaymentMethodDigits(customer, payment);
                  return (
                    <tr key={payment.id}>
                      <td>{formatShortDate(payment.createdAt)}</td>
                      <td>{getCustomerPaymentTypeLabel(payment.collectionType)}</td>
                      <td className={s.mono}>{digits}</td>
                      <td className={s.amountCell}>
                        {formatCustomerPaymentAmount(payment, currencyById)}
                      </td>
                      <td className={s.alignRight}>
                        <PaymentStatusBadge status={getUiPaymentStatus(payment)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={s.card}>
        <header className={s.cardHeader}>
          <h3 className={s.cardTitle}>Scheduled payments</h3>
        </header>
        {recentScheduledPayments.length === 0 ? (
          <div className={s.cardEmpty}>No scheduled payments.</div>
        ) : (
          <div className={s.cardBody}>
            <table className={s.miniTable}>
              <thead>
                <tr>
                  <th>Execute at</th>
                  <th>Time left</th>
                  <th>Descriptor</th>
                  <th>Subscription</th>
                  <th>Amount</th>
                  <th className={s.alignRight}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentScheduledPayments.map((sp) => (
                  <tr key={sp.id}>
                    <td>{formatShortDate(sp.executeAt)}</td>
                    <td>{getScheduledPaymentTimeLeft(sp.status, sp.executeAt)}</td>
                    <td>{sp.paymentDescriptor || '—'}</td>
                    <td>{getSubscriptionLabel(sp) || '—'}</td>
                    <td className={s.amountCell}>
                      {getScheduledPaymentAmountLabel(
                        sp,
                        sp.debtCollectionId ? debtById.get(sp.debtCollectionId) : undefined,
                        sp.subscriptionRecoveryId
                          ? recoveryById.get(sp.subscriptionRecoveryId)
                          : undefined,
                      )}
                    </td>
                    <td className={s.alignRight}>
                      <StatusChip
                        label={getScheduledPaymentStatusLabel(sp.status)}
                        tone={getScheduledPaymentStatusTone(sp.status)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={s.card}>
        <header className={s.cardHeader}>
          <h3 className={s.cardTitle}>Active subscriptions</h3>
        </header>
        {activeSubscriptions.length === 0 ? (
          <div className={s.cardEmpty}>No active subscriptions.</div>
        ) : (
          <div className={s.cardBody}>
            <ul className={s.cardList}>
              {activeSubscriptions.map((sub) => (
                <li key={sub.id} className={s.cardListItem}>
                  <div className={s.cardListBody}>
                    <div className={s.cardListTitle}>
                      {getSubscriptionLabel(sub) || sub.paymentDescriptor}
                    </div>
                    <div className={s.cardListSub}>
                      <span className={s.mono}>{sub.paymentDescriptor}</span>
                      {sub.paidTill && <> · Paid till {formatShortDate(sub.paidTill)}</>}
                    </div>
                  </div>
                  <SubscriptionBadge state="active" />
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className={s.card}>
        <header className={s.cardHeader}>
          <h3 className={s.cardTitle}>Payment methods</h3>
          {methods.length > 0 && <span className={s.countBadge}>{methods.length}</span>}
        </header>
        {methods.length === 0 ? (
          <div className={s.cardEmpty}>No payment methods.</div>
        ) : (
          <div className={s.cardBody}>
            <ul className={s.cardList}>
              {methods.map((method) => (
                <li key={method.id} className={s.cardListItem}>
                  <PaymentMethodBrandBadge method={method} />
                  <div className={s.cardListBody}>
                    <div className={`${s.cardListTitle} ${method.lastDigits ? s.mono : ''}`}>
                      {getPaymentMethodLabel(method)}
                    </div>
                    <div className={s.cardListSub}>{getPaymentMethodSubLabel(method)}</div>
                  </div>
                  <span className={s.statusPill}>{method.status}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className={s.card}>
        <header className={s.cardHeader}>
          <h3 className={s.cardTitle}>Activity</h3>
        </header>
        {activity.length === 0 ? (
          <div className={s.cardEmpty}>No activity yet.</div>
        ) : (
          <div className={s.cardBody}>
            <ul className={s.activityList}>
              {activity.map((event) => {
                const Icon = ACTIVITY_ICONS[event.kind];
                return (
                  <li key={event.key} className={s.activityItem}>
                    <span className={`${s.activityIcon} ${s[ACTIVITY_ICON_CLASSES[event.kind]]}`}>
                      <Icon sx={{ fontSize: 15 }} />
                    </span>
                    <div className={s.activityBody}>
                      <div className={s.activityTitle}>{event.title}</div>
                      <div className={s.activityDetail}>{event.detail}</div>
                    </div>
                    <span className={s.activityTime}>
                      {formatDistanceToNow(new Date(event.at), { addSuffix: true })}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

export default CustomerOverviewTab;
